import React, { FocusEvent, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { NavLink, useNavigate } from "react-router-dom";
import { Tag, Tooltip } from "antd";
import { useForm, Controller, Control } from "react-hook-form";
import { HiOutlineInformationCircle } from "react-icons/hi2";
import { settingsApi, toolsApi } from "../../api";
import {
  APP_ROUTES,
  IGetSettingsResponse,
  IResponseError,
  ICrmSettings,
} from "../../data-access";
import { useUser } from "../../hooks";
import { RootState, store } from "../../store";
import { storeSettingsActions } from "../../store/slices/settings";
import { storeToolsActions } from "../../store/slices/tools";
import {
  BackButton,
  FormButton,
  FormInput,
  FormSwitch,
  Panel,
} from "../../ui";
import { handleFailedRequest, notify } from "../../util";
import "./pipedrive-settings.scss";

interface IToggleProps {
  name: keyof ICrmSettings;
  control: Control<ICrmSettings>;
  disabled?: boolean;
  label: string;
  tooltipText?: string;
}

const ToggleSetting = ({
  name,
  control,
  disabled,
  label,
  tooltipText,
}: IToggleProps): JSX.Element => (
  <div className="setting">
    <div className="setting__label">
      {label}
      <Tooltip title={tooltipText} key={name}>
        <HiOutlineInformationCircle size={18} />
      </Tooltip>
    </div>
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, value, ref } }) => (
        <FormSwitch
          ref={ref}
          disabled={disabled}
          onChange={onChange}
          isChecked={!!value}
        />
      )}
    />
  </div>
);

export const PipedriveSettings = (): JSX.Element => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currentUser } = useUser();

  const [isLoading, setIsLoading] = useState(true);
  const [isPipedriveConnected, setIsPipedriveConnected] = useState(false);
  const [isProcessingForm, setIsProcessingForm] = useState(false);

  const isUserOnLitePlan =
    !!currentUser && currentUser?.plan === "Lite";

  useEffect(() => {
    Promise.all([toolsApi.getPipedriveStatus(), settingsApi.getSettings()])
      .then(([pipedriveRes, settingsRes]) => {
        const isConnected = pipedriveRes.data.is_connected;
        dispatch(
          storeToolsActions.setPipedriveConnection({
            is_connected: isConnected,
          })
        );
        setIsPipedriveConnected(isConnected);
        dispatch(
          storeSettingsActions.setCrmSettings({ ...settingsRes.data.crm })
        );
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, []);

  const storeBasedSettings = store.getState().settings.crm as ICrmSettings;

  const {
    register,
    control,
    watch,
    handleSubmit,
    setValue,
    getValues,
    reset,
    formState: { errors, dirtyFields, isValid },
  } = useForm<ICrmSettings>({
    mode: "all",
    defaultValues: {
      auto_create_company: storeBasedSettings.auto_create_company,
      create_lead: storeBasedSettings.create_lead,
      deal_name: storeBasedSettings.deal_name,
      create_deal: storeBasedSettings.create_deal,
      create_notes_for_job: storeBasedSettings.create_notes_for_job,
      create_notes_for_bid: storeBasedSettings.create_notes_for_bid,
      create_notes_for_all_messages:
        storeBasedSettings.create_notes_for_all_messages,
      create_all_notes_in_deal: storeBasedSettings.create_all_notes_in_deal,
    },
  });

  const handleFormSubmit = (formFields: ICrmSettings): void => {
    setIsProcessingForm(true);
    settingsApi
      .updateSettings(formFields)
      .then((response: IGetSettingsResponse) => {
        notify("success", "Settings have been successfully saved.");
        dispatch(storeSettingsActions.setCrmSettings({ ...response.data.crm }));
        reset(response.data.crm);
      })
      .catch((error: IResponseError) => {
        handleFailedRequest(error.status, error.text);
      })
      .finally(() => setIsProcessingForm(false));
  };

  const handleDealNameTagClick = (tag: string): void => {
    if (!isPipedriveConnected) return;
    const currentValue = getValues("deal_name") ?? "";
    const modifiedFieldValue = `${currentValue} ${tag}`
      .replace(/  +/g, " ")
      .trim();
    setValue("deal_name", modifiedFieldValue);
  };


  const isCreateDealEnabled = watch("create_deal");
  const isFormBeenModified = Object.keys(dirtyFields).length > 0;
  const isCRMElementDisabled = isProcessingForm || !isPipedriveConnected;

  if (isLoading) {
    return (
      <div className="pipedrive-settings">
        <BackButton to={APP_ROUTES.toolsAndApps}>
          Back to Tools & Apps
        </BackButton>
        <Panel className="pipedrive-settings__panel">
          <h3 className="pipedrive-settings__section-title">Sync Settings</h3>
          <div className="pipedrive-settings__toggles">
            <div className="setting">
              <div className="setting__label">Create the Contact's Company in the CRM automatically</div>
              <div className="pipedrive-settings__shimmer-switch" />
            </div>
            <div className="setting">
              <div className="setting__label">Create lead</div>
              <div className="pipedrive-settings__shimmer-switch" />
            </div>
          </div>

          <div className="pipedrive-settings__divider" />
          <h3 className="pipedrive-settings__section-title">Deal Configuration</h3>
          <div className="pipedrive-settings__toggles">
            <div className="setting">
              <div className="setting__label">Create deal</div>
              <div className="pipedrive-settings__shimmer-switch" />
            </div>
          </div>

          <div className="pipedrive-settings__divider" />
          <h3 className="pipedrive-settings__section-title">Notes & Activity</h3>
          <div className="pipedrive-settings__toggles">
            <div className="setting">
              <div className="setting__label">Create notes about Job in Contact or Deal</div>
              <div className="pipedrive-settings__shimmer-switch" />
            </div>
            <div className="setting">
              <div className="setting__label">Create notes about Bid in Contact or Deal</div>
              <div className="pipedrive-settings__shimmer-switch" />
            </div>
            <div className="setting">
              <div className="setting__label">Create notes about All messages in Contact or Deal</div>
              <div className="pipedrive-settings__shimmer-switch" />
            </div>
          </div>

          <div className="pipedrive-settings__save">
            <div className="pipedrive-settings__shimmer-btn" />
          </div>
        </Panel>
      </div>
    );
  }

  return (
    <div className="pipedrive-settings">
      <BackButton to={APP_ROUTES.toolsAndApps}>
        Back to Tools & Apps
      </BackButton>

      <Panel className="pipedrive-settings__panel">
        {isPipedriveConnected && !isUserOnLitePlan && (
          <form onSubmit={handleSubmit(handleFormSubmit)}>
            {/* Sync Settings */}
            <h3 className="pipedrive-settings__section-title">Sync Settings</h3>
            <div className="pipedrive-settings__toggles">
              <ToggleSetting
                control={control}
                disabled={isCRMElementDisabled}
                label="Create the Contact's Company in the CRM automatically"
                name="auto_create_company"
                tooltipText="When adding a contact to your CRM from Upwork, Upwex will also create a company profile for the contact's current company in your CRM (if the company doesn't exist for the client on Upwork, then Upwex will create a company in the CRM with the contact's name)."
              />
              <ToggleSetting
                control={control}
                disabled={isCRMElementDisabled}
                label="Create lead"
                name="create_lead"
                tooltipText="Enable this if you want a lead to be created in your CRM when synchronizing Upwork chat."
              />
            </div>

            {/* Deal Configuration */}
            <div className="pipedrive-settings__divider" />
            <h3 className="pipedrive-settings__section-title">
              Deal Configuration
            </h3>
            <div className="pipedrive-settings__toggles">
              <ToggleSetting
                control={control}
                disabled={isCRMElementDisabled}
                label="Create deal"
                name="create_deal"
                tooltipText="Enable this if you want a deal to be created in your CRM when synchronizing Upwork chat. Each new contract from the same client is a new deal."
              />
            </div>
            {isCreateDealEnabled && (
              <div className="pipedrive-settings__deal-config">
                <h4 className="pipedrive-settings__deal-config-title">
                  Deal Name Template *
                </h4>
                <FormInput
                  {...register("deal_name", {
                    required: "This field is required",
                  })}
                  value={watch("deal_name")}
                  placeholder="New deal with #contact of #company"
                  error={errors?.deal_name?.message}
                  disabled={isCRMElementDisabled}
                  onChange={(e: FocusEvent<HTMLInputElement>) =>
                    setValue("deal_name", e?.target?.value, {
                      shouldDirty: true,
                      shouldValidate: true,
                      shouldTouch: true,
                    })
                  }
                />
                <p className="pipedrive-settings__deal-config-note">
                  Change the default template for your new deal &nbsp;{" "}
                  <Tag
                    color="green"
                    onClick={() => handleDealNameTagClick("#contact")}
                  >
                    #contact
                  </Tag>
                  , &nbsp;
                  <Tag
                    color="green"
                    onClick={() => handleDealNameTagClick("#firstname")}
                  >
                    #firstname
                  </Tag>
                  , &nbsp;
                  <Tag
                    color="green"
                    onClick={() => handleDealNameTagClick("#lastname")}
                  >
                    #lastname
                  </Tag>
                  &nbsp; and &nbsp;
                  <Tag
                    color="green"
                    onClick={() => handleDealNameTagClick("#company")}
                  >
                    #company
                  </Tag>
                  &nbsp; will be replaced with the actual deal's contacts and
                  company name.
                </p>
              </div>
            )}

            {/* Notes & Activity */}
            <div className="pipedrive-settings__divider" />
            <h3 className="pipedrive-settings__section-title">
              Notes & Activity
            </h3>
            <div className="pipedrive-settings__toggles">
              <ToggleSetting
                control={control}
                disabled={isCRMElementDisabled}
                label="Create notes about Job in Contact or Deal"
                name="create_notes_for_job"
                tooltipText="Enable creation of Job Details note. Includes: Job ID, Job Date, Country, City, Job Title, Job Description."
              />
              <ToggleSetting
                control={control}
                disabled={isCRMElementDisabled}
                label="Create notes about Bid in Contact or Deal"
                name="create_notes_for_bid"
                tooltipText="Enable creation of Bid Details note. Includes: Job ID, Bid Date, Proposed rate, and Cover letter."
              />
              <ToggleSetting
                control={control}
                disabled={isCRMElementDisabled}
                label="Create notes about All messages in Contact or Deal"
                name="create_notes_for_all_messages"
                tooltipText="Enables the creation of a note with the history of all messages."
              />
            </div>

            {/* Save */}
            <div className="pipedrive-settings__save">
              <FormButton
                theme="green-light"
                htmlType="submit"
                loading={isProcessingForm}
                disabled={!isFormBeenModified || !isValid || isProcessingForm}
              >
                Save
              </FormButton>
            </div>
          </form>
        )}

        {/* Lite plan */}
        {isUserOnLitePlan && (
          <div className="pipedrive-settings__restricted">
            <div className="pipedrive-settings__divider" />
            This functionality is not available for <b>Lite</b> plan. Please
            upgrade your{" "}
            <NavLink className="pipedrive-settings__link" to={APP_ROUTES.plans}>
              plan
            </NavLink>{" "}
            to unlock it.
          </div>
        )}

        {/* Not connected */}
        {!isPipedriveConnected && !isUserOnLitePlan && (
          <div className="pipedrive-settings__not-connected">
            <div className="pipedrive-settings__divider" />
            <p>
              Pipedrive is not connected. Connect it from the Tools & Apps page
              to configure CRM settings.
            </p>
            <FormButton
              theme="green-light"
              onClick={() => navigate(APP_ROUTES.toolsAndApps)}
            >
              Go to Tools & Apps
            </FormButton>
          </div>
        )}
      </Panel>
    </div>
  );
};

export default PipedriveSettings;
